/**
 * MeetingChip — M-Rhythm-2 / M-Rhythm-3 / F-Meeting-1 Wave 2
 *
 * Compact chip rendered on calendar day cells for each meeting occurrence.
 * Color/icon by meeting_type; click opens:
 *   - /roros/meetings/:id/run if status is 'in_progress' or 'paused' (F-Meeting-1 Wave 2)
 *   - MeetingEditModal if user has roros.meetings.write permission (M-Rhythm-3)
 *   - MeetingDetailReadOnlyModal otherwise (M-Rhythm-2 behavior preserved)
 *
 * F-Meeting-1 Wave 2 additions:
 *   - 'paused' status chip (AC-4.3): pause icon overlay, same appearance as scheduled
 *   - Clicking in_progress or paused navigates directly to /roros/meetings/:id/run
 *
 * Compliance: M-Rhythm-3 plan §5 task 10 — permission-gated modal selection.
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pause } from 'lucide-react';
import { cn } from '@/lib/utils';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import { Loader2 } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import type { CalendarMeetingDTO, MeetingType, MeetingStatus, RorosMeetingDTO } from '@/types/roros/meetings';
import { MeetingDetailReadOnlyModal } from './MeetingDetailReadOnlyModal';
import { MeetingEditModal } from './MeetingEditModal';
import { isTerminalStatus } from './meeting-status-utils';

// ─── Color variants by meeting type ───────────────────────────────────────────

const TYPE_COLORS: Record<MeetingType, string> = {
  daily:     'bg-muted/60 text-muted-foreground border-border',
  weekly:    'bg-primary-50 text-primary-900 border-primary-200',
  monthly:   'bg-purple-50 text-purple-900 border-purple-200',
  quarterly: 'bg-orange-50 text-orange-900 border-orange-200',
  trimester: 'bg-orange-50 text-orange-900 border-orange-200',
  annual:    'bg-error-50 text-error-900 border-error-200',
  ad_hoc:    'bg-muted text-foreground border-border',
};

// ─── Status decorators ─────────────────────────────────────────────────────────

function statusDecorators(status: MeetingStatus): { lineThrough: boolean; opacity: string; checkmark: boolean } {
  // 'paused': no lineThrough, no opacity reduction, no checkmark — same as scheduled
  return {
    lineThrough: status === 'skipped' || status === 'cancelled',
    opacity: isTerminalStatus(status) ? 'opacity-50' : '',
    checkmark: status === 'completed',
  };
}

// ─── Absorbed badge icon ───────────────────────────────────────────────────────

/** Small ↑ arrow icon indicating this meeting was absorbed by a higher-cadence meeting. */
function AbsorbedIcon() {
  return (
    <span
      className="shrink-0 ml-0.5 text-[10px] leading-none"
      aria-hidden="true"
      title=""
    >
      ↑
    </span>
  );
}

// ─── Pause icon overlay (AC-4.3) ─────────────────────────────────────────────

/** Small ⏸ icon indicating this meeting is paused. */
function PauseIconOverlay() {
  return (
    <Pause
      className="h-2.5 w-2.5 shrink-0 text-warning-600"
      aria-hidden="true"
    />
  );
}

// ─── Format time from ISO string ──────────────────────────────────────────────

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface MeetingChipProps {
  meeting: CalendarMeetingDTO;
  className?: string;
  /** Called after a successful save/update so the parent can refetch. */
  onUpdate?: () => void;
}

export function MeetingChip({ meeting, className, onUpdate }: MeetingChipProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canWrite = hasPermission('roros.meetings.write');
  const navigate = useNavigate();

  const type = meeting.meetingType as MeetingType;
  const status = meeting.status as MeetingStatus;
  const { lineThrough, opacity, checkmark } = statusDecorators(status);
  const colorClass = TYPE_COLORS[type] ?? TYPE_COLORS.ad_hoc;
  const time = formatTime(meeting.scheduledAt);

  // Wave 10: detected if this meeting was absorbed by a higher-cadence meeting
  const absorbedByMeetingId = (meeting as RorosMeetingDTO).absorbedByMeetingId ?? null;
  const isAbsorbed = Boolean(absorbedByMeetingId);

  // Wave 4B: "Customized" badge — non-empty manualFieldOverrides indicates user edited this occurrence
  const manualFieldOverrides = (meeting as RorosMeetingDTO).manualFieldOverrides ?? [];
  const isCustomized = manualFieldOverrides.length > 0;
  const absorbedTooltip = isAbsorbed
    ? t('Absorbed by higher-cadence meeting on the same day')
    : undefined;

  // F-Meeting-1 Wave 2: in_progress or paused chips navigate directly to the run page
  const isFacilitating = status === 'in_progress' || status === 'paused';
  const isPaused = status === 'paused';

  const location = useLocation();

  const handleClose = () => setIsModalOpen(false);
  const handleEditClose = () => { setIsEditModalOpen(false); setIsModalOpen(false); };
  const handleSaved = () => {
    setIsEditModalOpen(false);
    setIsModalOpen(false);
    onUpdate?.();
  };

  const handleClick = () => {
    if (permissionsLoading) return;
    // AC-4.3: in_progress or paused → navigate to the run wizard directly
    // AC-MUT2-9: pass current URL as returnTo so wizard back-arrow / finish link returns here
    if (isFacilitating && meeting.id) {
      navigate(`/roros/meetings/${meeting.id}/run`, {
        state: { returnTo: location.pathname + location.search },
      });
      return;
    }
    setIsModalOpen(true);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        aria-label={t('Open meeting')}
        onClick={handleClick}
        aria-busy={permissionsLoading}
        title={absorbedTooltip}
        className={cn(
          'w-full text-left px-2 py-1 rounded-md border text-xs h-auto',
          'hover:opacity-90',
          'transition-opacity',
          colorClass,
          isAbsorbed ? 'opacity-50' : opacity,
          className,
        )}
        data-testid={`meeting-chip-${meeting.cadenceScheduleId ?? meeting.id ?? 'virtual'}`}
        data-absorbed={isAbsorbed ? 'true' : undefined}
      >
        <div className="flex items-center gap-1 min-w-0">
          {checkmark && (
            <svg className="h-3 w-3 shrink-0 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {/* AC-4.3: pause icon overlay for paused meetings */}
          {isPaused && <PauseIconOverlay />}
          {permissionsLoading && (
            <Loader2 className="h-2.5 w-2.5 animate-spin shrink-0 opacity-50" aria-hidden="true" />
          )}
          {time && (
            <span className="shrink-0 font-mono text-[10px] opacity-70">{time}</span>
          )}
          <span
            className={cn('truncate', (lineThrough || isAbsorbed) && 'line-through')}
            title={meeting.title}
          >
            {meeting.title}
          </span>
          {/* Wave 10: absorbed indicator */}
          {isAbsorbed && <AbsorbedIcon />}
          {meeting.isRecurringException && !isAbsorbed && (
            <span className="shrink-0 ml-1 rounded bg-warning-100 text-warning-700 px-1 text-[9px] font-semibold uppercase tracking-wide">
              {t('Edited')}
            </span>
          )}
          {/* Wave 4B: Customized badge — shown when user has manually overridden any template field */}
          {isCustomized && !isAbsorbed && (
            <span
              className="shrink-0 ml-1 rounded bg-blue-100 text-blue-700 px-1 text-[9px] font-semibold uppercase tracking-wide"
              data-testid="meeting-chip-customized-badge"
            >
              {t('Customized')}
            </span>
          )}
        </div>
      </Button>

      {/* AC-4.4 (OQ-W2-1): For scheduled meetings, always show the detail modal first
          so the "Run Meeting" button is accessible. Pass onEdit to open the edit modal.
          For non-scheduled, non-facilitating meetings (completed/cancelled/skipped):
          write-permission users see the edit modal directly. */}
      {isModalOpen && !isFacilitating && (
        meeting.status === 'scheduled' ? (
          <MeetingDetailReadOnlyModal
            meeting={meeting}
            isOpen={isModalOpen}
            onClose={handleClose}
            onEdit={canWrite && meeting.id ? () => { setIsModalOpen(false); setIsEditModalOpen(true); } : undefined}
          />
        ) : canWrite && meeting.id ? (
          <MeetingEditModal
            mode="edit"
            meetingId={meeting.id}
            isOpen={isModalOpen}
            onClose={handleClose}
            onSaved={handleSaved}
          />
        ) : (
          <MeetingDetailReadOnlyModal
            meeting={meeting}
            isOpen={isModalOpen}
            onClose={handleClose}
          />
        )
      )}
      {/* Edit modal opened from detail modal "Edit" button */}
      {isEditModalOpen && meeting.id && canWrite && (
        <MeetingEditModal
          mode="edit"
          meetingId={meeting.id}
          isOpen={isEditModalOpen}
          onClose={handleEditClose}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
